import React, { useRef, useEffect, useCallback } from 'react';
import TestTube from 'lucide-react/dist/esm/icons/test-tube';
import { Spinner } from '../components/ui';
import { UploadSection, ResultsDisplay } from '../features/analysis';
import { AnalyticsDashboard, AnalysisSidebar } from '../features/dashboard';
import { ComparisonView, ComparisonSelector } from '../features/comparison';
import { OrgManagementView } from '../features/org-management';
import { PerformanceView } from '../features/performance';
import { SettingsSidebar, PlatformSettingsView } from '../features/settings';
import { LoadingSpinner, ConfirmationDialog, Walkthrough, useToast } from '../components';
import { useWalkthrough } from '../components/feedback/Walkthrough';
import { uploadDocument, getAnalysisResult, extendAnalysis } from '../services/api';
import { getTranslation } from '../i18n';
import { trackEvent, trackPageView, trackError } from '../utils/tracking';
import { useAnalysisStore, useChatStore, useUIStore, useOrgStore, useImpersonationStore } from '../stores';
import AppSidebar from '../features/dashboard/components/AppSidebar';
import DashboardHeader from '../features/dashboard/components/DashboardHeader';
import ImpersonationBanner from '../features/dashboard/components/ImpersonationBanner';
import DashboardFooter from '../features/dashboard/components/DashboardFooter';
import ChatPanel from '../features/dashboard/components/ChatPanel';
import DuplicateDialog from '../features/dashboard/components/DuplicateDialog';
import { DiscoveryPortal } from '../features/discovery';
import { SurveyPortal } from '../features/survey';
import { AmlView, AmlSidebar } from '../features/aml';
import { SalesView, SalesSidebar } from '../features/sales';
import PerformanceSidebar from '../features/performance/components/PerformanceSidebar';

interface DashboardPageProps {
  onLogout: () => void;
}

const DashboardPage: React.FC<DashboardPageProps> = ({ onLogout }) => {
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const toast = useToast();

  // ── Stores ──
  const analysis = useAnalysisStore();
  const chat = useChatStore();
  const ui = useUIStore();

  const t = (key: string) => getTranslation(ui.language, key);

  const {
    showNewUserWalkthrough,
    showAnalysisWalkthrough,
    closeNewUserWalkthrough,
    closeAnalysisWalkthrough
  } = useWalkthrough(analysis.analysesList.length, !!analysis.results);

  const { fetchUserContext, clearContext, userContext, contextLoading } = useOrgStore();
  const hydrateImpersonation = useImpersonationStore((s) => s.hydrate);

  const AML_ROLES = ['aml_super_admin', 'aml_client_admin', 'aml_operator', 'aml_reviewer'];
  const isAmlOnlyUser = userContext ? AML_ROLES.includes(userContext.system_role) : false;
  const contextReady = !!userContext && !contextLoading;

  // ── Lifecycle ──
  useEffect(() => {
    trackPageView('Dashboard');
    hydrateImpersonation();
    fetchUserContext();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!userContext) return;
    if (AML_ROLES.includes(userContext.system_role)) {
      if (ui.activeModule !== 'aml-compliance') {
        ui.setActiveModule('aml-compliance');
      }
    } else {
      analysis.fetchAnalysesList();
    }
  }, [userContext?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    };
  }, []);

  // Re-fetch history list whenever a new job result arrives
  const currentJobId = analysis.currentJobId;
  const hasResults = !!analysis.results;
  useEffect(() => {
    if (hasResults && currentJobId) analysis.fetchAnalysesList();
  }, [currentJobId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset chat when no analysis selected
  useEffect(() => {
    if (!analysis.currentJobId) chat.resetChat();
  }, [analysis.currentJobId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Orchestration handlers ──

  const handleFileSelect = useCallback(async (files: any, userGuidance: string | null = null, forceReanalyze = false) => {
    analysis.setLoadingMode('analyzing');
    analysis.setLoading(true);
    analysis.setError(null);
    ui.setShowUpload(false);

    const fileArray = Array.isArray(files) ? files : [files];
    const fileNames = fileArray.map((f: any) => f.name).join(', ');
    analysis.setFileName(fileNames);

    const uploadStartTime = Date.now();
    try {
      trackEvent('Document Upload Started', {
        fileCount: fileArray.length,
        fileNames,
        totalSize: fileArray.reduce((sum: number, f: any) => sum + f.size, 0),
        hasGuidance: !!userGuidance
      });
      const response = await uploadDocument(fileArray, userGuidance, forceReanalyze);

      if (response.status === 'duplicate_detected') {
        analysis.setLoading(false);
        ui.setShowUpload(true);
        const existing = response.existing_analysis!;
        const companyDisplay = existing.company_name !== 'Unknown'
          ? `${existing.company_name} (${existing.fiscal_years})`
          : existing.filenames;
        analysis.setPendingUpload({ files: fileArray, guidance: userGuidance });
        analysis.setDuplicateDialogData({ analysisId: existing.analysis_id, companyDisplay, createdAt: existing.created_at });
        trackEvent('Duplicate Detected', { existingAnalysisId: existing.analysis_id });
        return;
      }

      const jobId = response.job_id!;
      analysis.setCurrentJobId(jobId);
      analysis.pollAnalysisStatus(jobId, uploadStartTime, pollTimeoutRef);
    } catch (err: any) {
      console.error('Upload error:', err);
      trackError('Upload Failed', err.message);
      analysis.setError(err.response?.data?.detail || t('errorMessage'));
      analysis.setLoading(false);
    }
  }, [analysis, ui, t]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleExtendAnalysis = useCallback(async (analysisId: string, filesOrGuidance: any = null, guidanceParam: string | null = null) => {
    const isFilesProvided = Array.isArray(filesOrGuidance);

    if (isFilesProvided && filesOrGuidance.length > 0) {
      const files = filesOrGuidance;
      const guidance = guidanceParam || '';
      analysis.setLoadingMode('analyzing');
      analysis.setLoading(true);
      analysis.setError(null);
      const fileArray = Array.isArray(files) ? files : [files];
      analysis.setFileName(fileArray.map((f: any) => f.name).join(', '));
      const uploadStartTime = Date.now();
      try {
        trackEvent('Analysis Extended', { analysisId, newFileCount: fileArray.length });
        toast.loading(t('extendingAnalysis') || 'Extending analysis with new documents...');
        const response = await extendAnalysis(analysisId, fileArray, guidance);
        analysis.setCurrentJobId(response.job_id);
        analysis.pollAnalysisStatus(response.job_id, uploadStartTime, pollTimeoutRef);
      } catch (err: any) {
        console.error('Extend analysis error:', err);
        trackError('Extend Analysis Failed', err.message);
        toast.error(err.response?.data?.detail || t('errorMessage'));
        analysis.setLoading(false);
      }
    } else {
      const suggestedGuidance = typeof filesOrGuidance === 'string' ? filesOrGuidance : null;
      analysis.setExtendingAnalysisId(analysisId);
      analysis.setExtensionGuidance(suggestedGuidance || '');
      ui.setShowUpload(true);
      toast.info(t('addMoreDocuments') || 'Upload additional documents to enhance this analysis');
      trackEvent('Extend Analysis Started', { analysisId });
    }
  }, [analysis, ui, toast, t]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleExtensionFileSelect = useCallback(async (files: any, userGuidance: string | null = null) => {
    if (!analysis.extendingAnalysisId) {
      handleFileSelect(files, userGuidance);
      return;
    }
    analysis.setLoadingMode('analyzing');
    analysis.setLoading(true);
    analysis.setError(null);
    ui.setShowUpload(false);
    const fileArray = Array.isArray(files) ? files : [files];
    analysis.setFileName(fileArray.map((f: any) => f.name).join(', '));
    const uploadStartTime = Date.now();
    try {
      trackEvent('Analysis Extended', { analysisId: analysis.extendingAnalysisId, newFileCount: fileArray.length });
      toast.loading(t('extendingAnalysis') || 'Extending analysis with new documents...');
      const response = await extendAnalysis(analysis.extendingAnalysisId, fileArray, userGuidance || analysis.extensionGuidance);
      analysis.setCurrentJobId(response.job_id);
      analysis.setExtendingAnalysisId(null);
      analysis.setExtensionGuidance('');
      analysis.pollAnalysisStatus(response.job_id, uploadStartTime, pollTimeoutRef);
      toast.success(t('analysisExtended') || 'Analysis extended successfully!');
    } catch (err: any) {
      console.error('Extension error:', err);
      trackError('Extension Failed', err.message);
      analysis.setError(err.response?.data?.detail || t('errorMessage'));
      analysis.setLoading(false);
      analysis.setExtendingAnalysisId(null);
      toast.error('Failed to extend analysis: ' + (err.response?.data?.detail || err.message));
    }
  }, [analysis, ui, toast, t, handleFileSelect]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectAnalysis = useCallback(async (analysisId: string) => {
    analysis.setLoadingMode('opening');
    analysis.setLoading(true);
    analysis.setError(null);
    analysis.setResults(null);
    ui.setShowUpload(false);
    chat.resetChat();
    try {
      trackEvent('Analysis Selected', { analysisId });
      const result = await getAnalysisResult(analysisId);
      analysis.setResults(result);
      analysis.setCurrentJobId(analysisId);
      const item = analysis.analysesList.find((a: any) => a.analysis_id === analysisId);
      if (item) {
        analysis.setFileName(item.document_name || '');
        analysis.setCurrentCompanyName(item.company_name || (result as any)?.company_name || 'Analysis');
      }
      chat.loadChatHistory(analysisId);
      analysis.setLoading(false);
    } catch (err: any) {
      console.error('Error loading analysis:', err);
      trackError('Analysis Load Failed', err.message);
      toast.error('Failed to load analysis', 'Error');
      analysis.setError(err.response?.data?.detail || 'Failed to load analysis');
      analysis.setLoading(false);
    }
  }, [analysis, ui, chat, toast]);

  const handleNewAnalysis = useCallback(() => {
    analysis.setResults(null);
    analysis.setFileName('');
    analysis.setError(null);
    analysis.setCurrentJobId(null);
    ui.setShowUpload(true);
  }, [analysis, ui]);

  const handleGoToDashboard = useCallback(() => {
    analysis.setResults(null);
    analysis.setFileName('');
    analysis.setError(null);
    analysis.setCurrentJobId(null);
    analysis.setCurrentCompanyName('');
    analysis.setComparisonData(null);
    ui.setShowUpload(false);
    chat.resetChat();
    trackEvent('Navigate to Dashboard');
    analysis.fetchAnalysesList();
  }, [analysis, ui, chat]);

  const handleLoadTestData = useCallback(async () => {
    const { loadTestAnalysis } = await import('../services/api');
    analysis.setLoadingMode('analyzing');
    analysis.setLoading(true);
    analysis.setError(null);
    ui.setShowUpload(false);
    analysis.setFileName('Test Data - GSI España');
    try {
      const response = await loadTestAnalysis();
      analysis.setResults(response.result);
      analysis.setCurrentJobId(response.job_id);
      analysis.setLoading(false);
    } catch (err: any) {
      console.error('Test data load error:', err);
      analysis.setError(err.response?.data?.detail || 'Failed to load test data');
      analysis.setLoading(false);
    }
  }, [analysis, ui]);

  const handleLogoutClick = useCallback(() => {
    ui.openLogoutDialog();
  }, [ui]);

  const stopImpersonation = useImpersonationStore((s) => s.stopImpersonation);
  const impersonationActive = useImpersonationStore((s) => s.active);

  const handleLogoutConfirm = useCallback(() => {
    trackEvent('User Logout');
    if (impersonationActive) {
      stopImpersonation();
      return;
    }
    clearContext();
    onLogout();
  }, [onLogout, clearContext, impersonationActive, stopImpersonation]);

  const handleDeleteClick = useCallback((a: any) => {
    ui.openDeleteDialog(a);
  }, [ui]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!ui.analysisToDelete) return;
    ui.setDeletingAnalysis(true);
    try {
      const { deleteAnalysis } = await import('../services/api');
      await deleteAnalysis(ui.analysisToDelete.analysis_id);
      trackEvent('Analysis Deleted', { analysisId: ui.analysisToDelete.analysis_id });
      useAnalysisStore.setState((s) => ({
        analysesList: s.analysesList.filter((a: any) => a.analysis_id !== ui.analysisToDelete.analysis_id)
      }));
      if (analysis.currentJobId === ui.analysisToDelete.analysis_id) {
        analysis.setResults(null);
        analysis.setCurrentJobId(null);
        analysis.setFileName('');
        analysis.setCurrentCompanyName('');
        chat.resetChat();
      }
      toast.success(`"${ui.analysisToDelete.company_name}" deleted successfully`, 'Analysis Deleted');
      ui.closeDeleteDialog();
    } catch (err: any) {
      console.error('Failed to delete analysis:', err);
      trackError('Delete Analysis Failed', err.message);
      toast.error('Failed to delete analysis. Please try again.', 'Error');
    } finally {
      ui.setDeletingAnalysis(false);
    }
  }, [ui, analysis, chat, toast]);

  const handleCompare = useCallback(async (id1: string, id2: string) => {
    try {
      await analysis.handleCompare(id1, id2);
      ui.setShowComparisonSelector(false);
      chat.setChatOpen(false);
    } catch {
      toast.error('Failed to compare analyses', 'Comparison Error');
    }
  }, [analysis, ui, chat, toast]);

  const handleDuplicateViewExisting = useCallback(() => {
    if (analysis.duplicateDialogData) {
      trackEvent('Duplicate - View Existing', { analysisId: analysis.duplicateDialogData.analysisId });
      handleSelectAnalysis(analysis.duplicateDialogData.analysisId);
    }
    analysis.setDuplicateDialogData(null);
    analysis.setPendingUpload(null);
  }, [analysis, handleSelectAnalysis]);

  const handleDuplicateReanalyze = useCallback(() => {
    if (analysis.pendingUpload) {
      trackEvent('Duplicate - Reanalyze');
      handleFileSelect(analysis.pendingUpload.files, analysis.pendingUpload.guidance, true);
    }
    analysis.setDuplicateDialogData(null);
    analysis.setPendingUpload(null);
  }, [analysis, handleFileSelect]);

  const handleDuplicateCancel = useCallback(() => {
    analysis.setDuplicateDialogData(null);
    analysis.setPendingUpload(null);
  }, [analysis]);

  const isTestUser = process.env.REACT_APP_ENABLE_TEST_DATA === 'true';

  // Determine if the Tier 2 (contextual) sidebar is visible for the active module
  const showAnalysisSidebar = contextReady && ui.activeModule === 'financial-forensics';
  const showSettingsSidebar = contextReady && ui.activeModule === 'org-management';
  const showAmlSidebar = contextReady && ui.activeModule === 'aml-compliance';
  const showPerformanceSidebar = contextReady && ui.activeModule === 'performance';
  const showSalesSidebar = contextReady && ui.activeModule === 'sales';
  const showT2Sidebar = showAnalysisSidebar || showSettingsSidebar || showAmlSidebar || showPerformanceSidebar || showSalesSidebar;
  const t2Open = (showAnalysisSidebar && ui.sidebarOpen) || showSettingsSidebar || showAmlSidebar || showPerformanceSidebar || showSalesSidebar;

  // Main content left padding: T1 (60px) + T2 (240px when open) + 16px gap
  const mainPaddingLeft = t2Open ? 'pl-[316px]' : 'pl-[76px]';

  // ── Render ──

  return (
    <div className={`h-screen flex flex-col relative overflow-hidden ${t2Open ? 'sidebar-open' : ''}`}>
      <ImpersonationBanner />
      <DashboardHeader
        language={ui.language}
        results={analysis.results}
        showUpload={ui.showUpload}
        currentCompanyName={analysis.currentCompanyName}
        analysesCount={analysis.analysesList.length}
        onGoToDashboard={handleGoToDashboard}
        onLanguageChange={ui.setLanguage}
        onLogoutClick={handleLogoutClick}
        onLogoutConfirm={handleLogoutConfirm}
      />

      {/* Comparison Selector Modal */}
      {ui.showComparisonSelector && (
        <ComparisonSelector
          analyses={analysis.analysesList}
          onCompare={handleCompare}
          onClose={() => ui.setShowComparisonSelector(false)}
          language={ui.language}
          isLoading={analysis.comparisonLoading}
        />
      )}

      {/* Main Content */}
      <main className={`flex-1 pt-2 pb-12 animate-fade-in overflow-y-auto overflow-x-hidden min-h-0 flex flex-col relative bg-surface-page transition-[padding] duration-300 ${mainPaddingLeft} pr-6`}>
        <div
          className={`w-full mx-auto flex-1 flex flex-col min-h-0 ${
            ui.activeModule === 'sales' ? 'max-w-none' : 'max-w-[1400px]'
          }`}
        >
          {!contextReady && (
            <div className="flex items-center justify-center h-64 text-content-muted">
              <Spinner size="md" />
            </div>
          )}

          {/* ── Financial Forensics Module ── */}
          {contextReady && ui.activeModule === 'financial-forensics' && (
            <>
              {/* Comparison View */}
              {analysis.comparisonData && (
                <ComparisonView
                  analysis1={analysis.comparisonData.analysis1}
                  analysis2={analysis.comparisonData.analysis2}
                  comparison={analysis.comparisonData.comparison}
                  onClose={() => analysis.setComparisonData(null)}
                  language={ui.language}
                />
              )}

              {!analysis.loading && !analysis.results && !analysis.error && !ui.showUpload && !analysis.comparisonData && (
                <AnalyticsDashboard
                  onNewAnalysis={handleNewAnalysis}
                  onSelectAnalysis={handleSelectAnalysis}
                  analysesList={analysis.analysesList}
                  language={ui.language}
                />
              )}

              {!analysis.loading && !analysis.results && !analysis.error && ui.showUpload && !analysis.comparisonData && (
                <>
                  <UploadSection
                    onFileSelect={analysis.extendingAnalysisId ? handleExtensionFileSelect : handleFileSelect}
                    language={ui.language}
                    initialGuidance={analysis.extensionGuidance}
                    isExtending={!!analysis.extendingAnalysisId}
                  />
                  {isTestUser && (
                    <div className="text-center mt-8">
                      <button
                        className="py-4 px-8 text-base font-semibold bg-gradient-to-br from-status-success to-[#059669] text-content-inverse border-none rounded-xl cursor-pointer inline-flex items-center gap-2 shadow-[0_0_20px_rgba(16,185,129,0.4)] transition-all duration-300 hover:translate-y-[-2px] hover:shadow-[0_0_30px_rgba(16,185,129,0.6)]"
                        onClick={handleLoadTestData}
                      >
                        <TestTube size={20} />
                        Load Test Data (GSI España)
                      </button>
                    </div>
                  )}
                </>
              )}

              {analysis.loading && !analysis.comparisonData && (
                <LoadingSpinner language={ui.language} mode={analysis.loadingMode} />
              )}

              {analysis.error && !analysis.comparisonData && (
                <div className="flex justify-center items-center p-12 min-h-[400px] animate-fade-in">
                  <div className="p-12 rounded-xl text-center max-w-[500px] border border-status-error/30 glass-strong">
                    <h2 className="text-2xl font-bold mb-4 text-status-error-light">❌ {t('errorAnalysis')}</h2>
                    <p className="text-base text-secondary mb-8 leading-relaxed">{analysis.error}</p>
                    <button
                      className="py-3.5 px-6 bg-gradient-to-br from-status-error to-status-error-light border-none rounded-xl text-content-inverse text-sm font-bold cursor-pointer uppercase tracking-widest transition-all duration-250 hover:translate-y-[-2px] hover:shadow-[0_0_20px_rgba(239,68,68,0.4)]"
                      onClick={handleNewAnalysis}
                    >
                      {t('retryButton')}
                    </button>
                  </div>
                </div>
              )}

              {analysis.results && !analysis.loading && !analysis.comparisonData && (
                <>
                  {/* Document Info Bar */}
                  <div className="py-4 px-8 bg-gradient-to-br from-brand/15 to-[#00111c]/80 rounded-t-2xl ml-10 border-2 border-accent border-b-0 w-fit max-w-[calc(100%-40px)] shadow-[0_4px_20px_rgba(33,158,188,0.2)] animate-fade-in">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-secondary font-semibold uppercase tracking-widest opacity-70">File name:</span>
                      <span className="text-[0.9375rem] text-content font-semibold">{analysis.fileName}</span>
                    </div>
                  </div>

                  <ChatPanel analysisId={analysis.currentJobId} language={ui.language} />

                  <ResultsDisplay
                    data={analysis.results}
                    language={ui.language}
                    onLanguageChange={ui.setLanguage}
                    jobId={analysis.currentJobId}
                    analysisId={analysis.currentJobId}
                    onToggleChat={chat.toggleChat}
                    chatOpen={chat.chatOpen}
                    chatMessageCount={chat.chatMessages.length}
                    onExtendAnalysis={handleExtendAnalysis}
                  />
                </>
              )}
            </>
          )}

          {/* ── Performance Module (KPI Tracking) ── */}
          {contextReady && ui.activeModule === 'performance' && (
            <PerformanceView />
          )}

          {/* ── Settings Module (Organization + Platform) ── */}
          {contextReady && ui.activeModule === 'org-management' && ui.settingsScope === 'organization' && (
            <OrgManagementView />
          )}
          {contextReady && ui.activeModule === 'org-management' && ui.settingsScope === 'platform' && (
            <PlatformSettingsView />
          )}

          {/* ── AML Compliance Module ── */}
          {contextReady && ui.activeModule === 'aml-compliance' && (
            <AmlView />
          )}

          {/* ── Sales Module ── */}
          {contextReady && ui.activeModule === 'sales' && (
            <SalesView />
          )}
        </div>

        <DashboardFooter sidebarOpen={t2Open} language={ui.language} />
      </main>

      {/* Tier 1: App Module Navigation */}
      <AppSidebar />

      {/* Tier 2: Analysis History Sidebar (only for Financial Forensics) */}
      {showAnalysisSidebar && (
        <AnalysisSidebar
          isOpen={ui.sidebarOpen}
          onToggle={ui.toggleSidebar}
          analyses={analysis.analysesList}
          onSelectAnalysis={handleSelectAnalysis}
          onNewAnalysis={handleNewAnalysis}
          onDeleteAnalysis={handleDeleteClick}
          loading={analysis.historyLoading}
          currentAnalysisId={analysis.currentJobId}
          language={ui.language}
        />
      )}

      {/* Tier 2: Settings Scope Sidebar */}
      {showSettingsSidebar && (
        <SettingsSidebar />
      )}

      {/* Tier 2: AML Compliance Sidebar */}
      {showAmlSidebar && (
        <AmlSidebar />
      )}

      {/* Tier 2: Performance Module Sidebar */}
      {showPerformanceSidebar && (
        <PerformanceSidebar />
      )}

      {/* Tier 2: Sales Module Sidebar */}
      {showSalesSidebar && (
        <SalesSidebar />
      )}

      {/* Logout Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={ui.showLogoutDialog}
        onClose={ui.closeLogoutDialog}
        onConfirm={handleLogoutConfirm}
        title={t('confirmLogout')}
        message={t('confirmLogoutMessage')}
        confirmText={t('logout')}
        cancelText={t('cancel')}
        variant="default"
      />

      {/* Delete Analysis Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={ui.showDeleteDialog}
        onClose={ui.closeDeleteDialog}
        onConfirm={handleDeleteConfirm}
        title={t('confirmDelete')}
        message={`${t('confirmDeleteMessage')} "${ui.analysisToDelete?.company_name || 'this analysis'}"?`}
        confirmText={ui.deletingAnalysis ? t('deleting') : t('delete')}
        cancelText={t('cancel')}
        variant="danger"
      />

      {/* Duplicate Detection Dialog */}
      {analysis.duplicateDialogData && (
        <DuplicateDialog
          data={analysis.duplicateDialogData}
          language={ui.language}
          onViewExisting={handleDuplicateViewExisting}
          onReanalyze={handleDuplicateReanalyze}
          onCancel={handleDuplicateCancel}
        />
      )}

      {/* Walkthroughs */}
      <Walkthrough
        isOpen={showNewUserWalkthrough}
        onClose={closeNewUserWalkthrough}
        onComplete={() => trackEvent('Walkthrough New User Completed')}
        mode="new_user"
      />
      <Walkthrough
        isOpen={showAnalysisWalkthrough}
        onClose={closeAnalysisWalkthrough}
        onComplete={() => trackEvent('Walkthrough Analysis Completed')}
        mode="analysis_view"
      />

      {/* Discovery Portal Overlay */}
      {ui.discoveryPortalOpen && <DiscoveryPortal />}

      {/* Survey Portal Overlay */}
      {ui.surveyPortalOpen && <SurveyPortal />}

      {/* Custom keyframe animations */}
      <style>{`
        @keyframes chatSlideIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes messageSlideIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes typingBounce {
          0%, 80%, 100% { transform: scale(0.6); opacity: 0.5; }
          40% { transform: scale(1); opacity: 1; }
        }
        @keyframes gradientGlow {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
    </div>
  );
};

export default DashboardPage;
